import { useEffect, useState, type CSSProperties } from 'react';

/** Same colour as the canvas a run fills while it is being made. */
const PLACEHOLDER = '#e9e5dc';
/** After this many failures the host is told we are still at it. */
const SLOW_AFTER = 3;
/** Roughly two minutes of asking again; a picture that is coming has come by then. */
const GIVE_UP_AFTER = 14;

/**
 * A stored picture that owns its waiting: a neutral placeholder until it has loaded, and it asks
 * again when a load fails (a CSS background image never does, and shows whatever is behind it).
 * Key it by `src`. `hidden` still loads the file, so a carousel's next card is ready when reached.
 */
export function LoadingImage({
  src,
  alt,
  hidden,
  fill,
  imgStyle,
}: {
  src: string;
  alt: string;
  hidden?: boolean;
  /** Covers its positioned parent; otherwise it sits in the flow and holds a space while waiting. */
  fill?: boolean;
  imgStyle?: CSSProperties;
}) {
  const [loaded, setLoaded] = useState(false);
  const [failures, setFailures] = useState(0);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!failures || failures >= GIVE_UP_AFTER) return;
    const wait = setTimeout(
      () => setAttempt(failures),
      Math.min(1000 * 2 ** (failures - 1), 10_000),
    );
    return () => clearTimeout(wait);
  }, [failures]);
  const gaveUp = failures >= GIVE_UP_AFTER;
  return (
    <span
      data-media-state={loaded ? 'loaded' : 'loading'}
      hidden={hidden}
      style={{
        display: hidden ? 'none' : 'block',
        ...(fill ? { position: 'absolute', inset: 0 } : { position: 'relative' }),
        ...(loaded ? {} : { background: PLACEHOLDER, overflow: 'hidden' }),
        ...(loaded || fill ? {} : { minHeight: 200 }),
      }}
    >
      {!loaded && !gaveUp && <span className="shimmer" />}
      {!loaded && failures >= SLOW_AFTER && (
        <span
          role="status"
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            textAlign: 'center',
            fontSize: 13,
            color: '#555',
          }}
        >
          {gaveUp
            ? 'This picture did not load. Reload the page to try again.'
            : 'Still fetching this picture…'}
        </span>
      )}
      <img
        key={attempt}
        src={src}
        alt={alt}
        draggable={false}
        onLoad={() => setLoaded(true)}
        onError={() => setFailures((n) => n + 1)}
        style={{
          ...(fill ? { width: '100%', height: '100%', objectFit: 'cover' } : imgStyle),
          display: 'block',
          opacity: loaded ? 1 : 0,
        }}
      />
    </span>
  );
}
